import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CoordFields } from "@/components/network/CoordFields";
import { WilayahCombobox } from "@/components/network/WilayahCombobox";
import { INFRA_STATUS, STATUS_LABEL, SELECT_CLS, apiError } from "@/lib/network";

const blank = (refs) => ({ kode: "", nama: "", lokasi_id: refs?.default_lokasi_id || "", wilayah_id: "", alamat: "", latitude: "", longitude: "", status: "AKTIF", catatan: "" });

function F({ label, children }) {
  return <div><Label>{label}</Label>{children}</div>;
}

export function FdtFormDialog({ open, editing, refs, onClose, onSaved }) {
  const [form, setForm] = useState(blank(refs));
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(editing ? { ...blank(refs), ...editing, latitude: editing.latitude ?? "", longitude: editing.longitude ?? "" } : blank(refs));
  }, [open, editing, refs]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const wil = (refs?.wilayah || []).filter((w) => w.status_aktif !== false || w.wilayah_id === form.wilayah_id);
  const lok = refs?.locations.find((l) => l.id === form.lokasi_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.wilayah_id) return toast.error("Wilayah wajib dipilih");
    const num = (v) => (v === "" || v == null ? null : Number(v));
    const { kode, nama, lokasi_id, wilayah_id, alamat, status, catatan } = form;
    const payload = { kode: (kode || "").trim(), nama, lokasi_id, wilayah_id, alamat, status, catatan, latitude: num(form.latitude), longitude: num(form.longitude) };
    setSaving(true);
    try {
      const r = editing ? await api.put(`/network/fdt/${editing.id}`, payload) : await api.post("/network/fdt", payload);
      toast.success(`FDT ${r.data.kode} tersimpan`);
      onSaved(r.data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92dvh] max-w-2xl overflow-y-auto" data-testid="fdt-form-dialog">
        <DialogHeader><DialogTitle>{editing ? `Edit FDT ${editing.kode}` : "Tambah FDT"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <F label="Kode"><Input value={form.kode || ""} maxLength={40} className="font-mono-tab uppercase" data-testid="fdt-form-kode"
                                   onChange={(e) => setForm((f) => ({ ...f, kode: e.target.value.toUpperCase().replace(/\s/g, "") }))}
                                   placeholder={editing ? editing.kode : "Kosongkan = otomatis (FDT-xxx)"} /></F>
            <div className="sm:col-span-2"><F label="Nama FDT"><Input value={form.nama} onChange={set("nama")} required data-testid="fdt-form-nama" /></F></div>
            <F label="Status"><select className={`${SELECT_CLS} w-full`} value={form.status} onChange={set("status")} data-testid="fdt-form-status">
              {INFRA_STATUS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}</select></F>
            <div className="sm:col-span-2"><F label="Lokasi (Kantor/Cabang)"><select className={`${SELECT_CLS} w-full`} value={form.lokasi_id} onChange={set("lokasi_id")} required data-testid="fdt-form-lokasi">
              <option value="">— Pilih —</option>{(refs?.locations || []).filter((l) => l.active || l.id === form.lokasi_id).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></F></div>
            <div className="sm:col-span-3"><F label="Wilayah"><WilayahCombobox className="w-full" options={wil} value={form.wilayah_id} testid="fdt-form-wilayah"
                                                                 onChange={(v) => setForm((f) => ({ ...f, wilayah_id: v }))} /></F></div>
          </div>
          <F label="Alamat"><Input value={form.alamat} onChange={set("alamat")} data-testid="fdt-form-alamat" /></F>
          <CoordFields lat={form.latitude} lng={form.longitude} viewCenter={lok?.latitude != null ? [lok.latitude, lok.longitude] : null}
                       onChange={(la, ln) => setForm((f) => ({ ...f, latitude: la, longitude: ln }))} />
          <F label="Catatan"><Textarea rows={2} value={form.catatan} onChange={set("catatan")} data-testid="fdt-form-catatan" /></F>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" disabled={saving} data-testid="fdt-form-submit">{saving ? "Menyimpan…" : "Simpan"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
